import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, interval, of } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { VehicleTracking } from '../models/vehicle-tracking.model';
import { environment } from '../../environment';
import { getLatestByDate, normalizeApiArray } from '../utils/api-response.util';

type TrackingResponse = VehicleTracking[] | VehicleTracking | { $values?: VehicleTracking[] };

export interface PassengerTripMapItem {
  bookingID: number;
  bookingReference?: string | null;
  statusID?: number | null;
  statusName?: string | null;
  trip?: {
    tripID: number;
    tripName?: string | null;
    tripStartTime?: string | null;
    tripEndTime?: string | null;
    isActive?: boolean;
    isPast?: boolean;
  } | null;
  vehicle?: {
    vehicleID: number;
    vehicleLabel?: string | null;
    vehicleRegistration?: string | null;
    vehicleIdentificationNumber?: string | null;
  } | null;
  operator?: {
    organisationID?: number | null;
    orgName?: string | null;
    operatorCode?: string | null;
    orgLogo?: string | null;
  } | null;
  route?: {
    routeID?: number | null;
    routeName?: string | null;
    description?: string | null;
    stops?: PassengerRouteStop[] | { $values?: PassengerRouteStop[] };
  } | null;
  currentTracking?: VehicleTracking | null;
  nextStop?: PassengerNextStop | null;
  routeHistory?: VehicleTracking[] | { $values?: VehicleTracking[] };
}

export interface PassengerRouteStop {
  routeStopID?: number | null;
  stopName?: string | null;
  stopLatitude?: number | null;
  stopLongitude?: number | null;
  stopOrder?: number | null;
}

export interface PassengerNextStop extends PassengerRouteStop {
  distanceKm?: number | null;
  currentSpeedKmh?: number | null;
  estimatedMinutes?: number | null;
  estimatedArrivalUtc?: string | null;
  etaConfidence?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class VehicleTrackingService {
  private apiUrl = `${environment.apiUrl}/VehicleTracking`;
  private vehicleTrackingMap = new Map<number, VehicleTracking>();

  constructor(private http: HttpClient) { }

  getVehicleTracking(vehicleId: number): Observable<VehicleTracking[]> {
    return this.http.get<TrackingResponse>(`${this.apiUrl}/${vehicleId}`).pipe(
      map(response => normalizeApiArray<VehicleTracking>(response)),
      catchError(error => {
        console.error(`Error fetching tracking history for vehicle ${vehicleId}:`, error);
        return of([]);
      })
    );
  }

  getLatestVehicleTracking(vehicleId: number): Observable<VehicleTracking | null> {
    return this.getVehicleTracking(vehicleId).pipe(
      map(trackings => getLatestByDate(trackings, tracking => tracking.vehicleTrackingDateTime))
    );
  }

  startTrackingPolling(vehicleIds: number[], refreshMs = 30000): Observable<Map<number, VehicleTracking>> {
    return interval(refreshMs).pipe(
      startWith(0),
      switchMap(() => this.getAllVehiclesLatestTracking(vehicleIds))
    );
  }

  getPassengerTripMap(options: {
    bookingId?: number | null;
    tripId?: number | null;
    includeHistory?: boolean;
    historyTake?: number;
  } = {}): Observable<PassengerTripMapItem[]> {
    let params = new HttpParams();

    if (options.bookingId) {
      params = params.set('bookingId', String(options.bookingId));
    }

    if (options.tripId) {
      params = params.set('tripId', String(options.tripId));
    }

    if (options.includeHistory) {
      params = params.set('includeHistory', 'true');
      params = params.set('historyTake', String(options.historyTake || 250));
    }

    return this.http.get<unknown>(`${this.apiUrl}/passenger-trip-map`, { params }).pipe(
      map(response => normalizeApiArray<PassengerTripMapItem>(response))
    );
  }

  getAllVehiclesLatestTracking(vehicleIds: number[]): Observable<Map<number, VehicleTracking>> {
    if (!vehicleIds.length) {
      this.vehicleTrackingMap = new Map<number, VehicleTracking>();
      return of(this.vehicleTrackingMap);
    }

    const requests = vehicleIds.map(vehicleId =>
      this.getLatestVehicleTracking(vehicleId).pipe(
        map(tracking => ({ vehicleId, tracking })),
        catchError(error => {
          console.error(`Error fetching latest tracking for vehicle ${vehicleId}:`, error);
          return of({ vehicleId, tracking: null });
        })
      )
    );

    return forkJoin(requests).pipe(
      map(results => {
        const trackingMap = new Map<number, VehicleTracking>();

        results.forEach(result => {
          if (result.tracking) {
            trackingMap.set(result.vehicleId, result.tracking);
          }
        });

        this.vehicleTrackingMap = trackingMap;
        return trackingMap;
      })
    );
  }

  getCachedTracking(vehicleId: number): VehicleTracking | undefined {
    return this.vehicleTrackingMap.get(vehicleId);
  }

  getAllCachedTracking(): Map<number, VehicleTracking> {
    return this.vehicleTrackingMap;
  }
}
